import { c as resetAnalyticsUser, l as trackAnalyticsEvent, o as identifyAnalyticsUser } from "./Analytics-CHM4AGD9.js";
import { t as appendQueryParams } from "./utils-CT_1OHHh.js";
import { c as ApiError, t as ApiClient } from "./api-DjIIBiAX.js";
import { i as clearBrowserStorage, r as BrowserStorageType } from "./BrowserStorage-XBdkz9k6.js";
import { t as useLaunchDarklyUpdater } from "./FeatureFlags-OdhsaJ5u.js";
import constate from "constate";
import { useCallback, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery } from "react-query";
//#region src/Auth/types.ts
var UserRole = /* @__PURE__ */ function(UserRole) {
	UserRole["USER_WITH_ACCOUNT"] = "user_with_account";
	UserRole["NO_TEST"] = "no_test";
	UserRole["GENETICS"] = "genetics";
	UserRole["COVID_NON_DEPENDENT_PROXY"] = "covid_proxy";
	UserRole["COVID_FULL_USER_OR_DEPENDENT"] = "covid_full_user_or_dependent";
	UserRole["CARE_FULL_USER_OR_DEPENDENT"] = "care_full_user_or_dependent";
	UserRole["ESSENTIAL_CARE_USER"] = "essential_care_user";
	UserRole["STI_TEST_USER"] = "sti_test_user";
	UserRole["COLLECTION_SITE_ADMIN"] = "collection_site_admin";
	UserRole["COVID_SITE_MANAGER"] = "covid_site_manager";
	UserRole["PROVIDER"] = "provider";
	UserRole["SUPPORT_STAFF"] = "support_staff";
	UserRole["COLOR_EMPLOYEE"] = "color_employee";
	UserRole["DASHBOARD_VIEWER"] = "dashboard_viewer";
	UserRole["ACCESS_MANAGER"] = "access_manager";
	UserRole["SUPPORT_ADMIN"] = "support_admin";
	UserRole["ELIGIBILITY_DATA_MANAGER"] = "eligibility_data_manager";
	UserRole["SAMPLE_REVIEWER"] = "sample_reviewer";
	UserRole["ORDERING_PHYSICIAN"] = "ordering_physician";
	UserRole["GENETIC_COUNSELOR"] = "genetic_counselor";
	UserRole["EXTERNAL_CLASSIFICATION_REVIEWER"] = "external_classification_reviewer";
	UserRole["INTERNAL_CLASSIFICATION_REVIEWER"] = "internal_classification_reviewer";
	UserRole["VARIANT_REVIEWER"] = "variant_reviewer";
	UserRole["VARIANT_REVIEWER_MANAGER"] = "variant_reviewer_manager";
	UserRole["GENETICS_BOARD_MEMBER"] = "genetics_board_member";
	UserRole["PATHOLOGIST"] = "pathologist";
	UserRole["FULFILLMENT_STAFF"] = "fulfillment_staff";
	UserRole["CONTACT_MANAGER"] = "contact_manager";
	UserRole["VACCINATION_PLATFORM_SITE_MANAGER"] = "vaccination_platform_site_manager";
	UserRole["VACCINATION_PLATFORM_STAFF_MANAGER"] = "vaccination_platform_staff_manager";
	UserRole["VACCINATION_PLATFORM_SUPER_ADMIN"] = "vaccination_platform_super_admin";
	UserRole["VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER"] = "vaccination_platform_site_inventory_manager";
	UserRole["VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER"] = "vaccination_platform_bulk_inventory_manager";
	UserRole["VACCINATION_PLATFORM_SITE_STAFF"] = "vaccination_platform_site_staff";
	UserRole["VACCINATION_PLATFORM_SCRIBE"] = "vaccination_platform_scribe";
	UserRole["VACCINATION_PLATFORM_VACCINATING_CLINICIAN"] = "vaccination_platform_vaccinating_clinician";
	UserRole["LAB_STAFF"] = "lab_staff";
	return UserRole;
}({});
var ALL_USER_ROLES = [
	"no_test",
	"genetics",
	"covid_proxy",
	"care_full_user_or_dependent",
	"covid_full_user_or_dependent",
	"essential_care_user",
	"sti_test_user",
	"covid_site_manager",
	"provider",
	"support_staff",
	"color_employee",
	"dashboard_viewer",
	"access_manager",
	"eligibility_data_manager",
	"collection_site_admin",
	"fulfillment_staff",
	"contact_manager",
	"vaccination_platform_site_manager",
	"vaccination_platform_staff_manager",
	"vaccination_platform_super_admin"
];
var GENETICS_USER_ROLES = ["genetics", "no_test"];
var CollectionSiteRole = /* @__PURE__ */ function(CollectionSiteRole) {
	CollectionSiteRole["ONSITE_STAFF"] = "sample_collection_admin";
	CollectionSiteRole["BATCH_SHIPMENT_ADMIN"] = "batch_shipment_admin";
	CollectionSiteRole["SITE_SCHEDULER"] = "site_scheduler";
	CollectionSiteRole["SITE_MANAGER"] = "site_manager";
	CollectionSiteRole["STAFF_MANAGER"] = "staff_manager";
	CollectionSiteRole["VACCINE_INVENTORY_MANAGER"] = "vaccine_inventory_manager";
	CollectionSiteRole["VACCINE_CLINICIAN"] = "vaccine_clinician";
	CollectionSiteRole["SCRIBE"] = "scribe";
	return CollectionSiteRole;
}({});
var CollectionSiteRoleCategory = /* @__PURE__ */ function(CollectionSiteRoleCategory) {
	CollectionSiteRoleCategory["GENERAL"] = "general";
	CollectionSiteRoleCategory["APPOINTMENT_MANAGEMENT"] = "appointment_management";
	CollectionSiteRoleCategory["INVENTORY_MANAGEMENT"] = "inventory_management";
	CollectionSiteRoleCategory["ONSITE_PATIENT_MANAGEMENT"] = "onsite_patient_management";
	CollectionSiteRoleCategory["BATCH_SHIPMENT"] = "batch_shipment";
	return CollectionSiteRoleCategory;
}({});
var UserPermission = /* @__PURE__ */ function(UserPermission) {
	UserPermission["PUT_SAMPLES_ON_HOLD"] = "samples.put_sample_on_hold";
	UserPermission["VIEW_USERS"] = "users.view_users";
	UserPermission["VIEW_PHI"] = "users.view_phi";
	UserPermission["VIEW_APPOINTMENTS"] = "counseling.view_appointments";
	UserPermission["VIEW_HEALTH_HISTORY"] = "health_profiles.view_healthhistory";
	UserPermission["VIEW_COVID_REPORTS_QUEUE"] = "results.can_view_covid_reports_queue";
	UserPermission["ACCESS_PATIENT_DATA"] = "colorhub.access_patient_data";
	UserPermission["ACCESS_SKIN_CLINIC"] = "colorhub.access_skin_clinic";
	UserPermission["EDIT_SKIN_CLINIC"] = "colorhub.edit_skin_clinic";
	UserPermission["REVIEW_SKIN_CLINIC"] = "colorhub.review_skin_clinic";
	UserPermission["VIEW_PATIENTS_HUB"] = "colorhub.view_patients_hub";
	UserPermission["USE_GLOBAL_SEARCH"] = "colorhub.use_global_search";
	UserPermission["VIEW_PATIENT_RECORD"] = "colorhub.view_patient_record";
	UserPermission["EDIT_PATIENT_DEMOGRAPHICS"] = "colorhub.edit_patient_demographics";
	UserPermission["EDIT_PATIENT_CONTACT_INFO"] = "colorhub.edit_patient_contact_info";
	UserPermission["EDIT_PATIENT_INSURANCE"] = "colorhub.edit_patient_insurance";
	UserPermission["ADD_PATIENT_NOTES"] = "colorhub.add_patient_notes";
	UserPermission["EDIT_PATIENT_NOTES"] = "colorhub.edit_patient_notes";
	UserPermission["ARCHIVE_PATIENT_NOTES"] = "colorhub.archive_patient_notes";
	UserPermission["VIEW_PATIENT_TIMELINE"] = "colorhub.view_patient_timeline";
	UserPermission["VIEW_PATIENT_GOALS"] = "colorhub.view_patient_goals";
	UserPermission["EDIT_PATIENT_GOALS"] = "colorhub.edit_patient_goals";
	UserPermission["ADD_TASK"] = "colorhub.add_task";
	UserPermission["EDIT_TASK"] = "colorhub.edit_task";
	UserPermission["ADD_TASK_COMMENTS"] = "colorhub.add_task_comments";
	UserPermission["EDIT_TASK_COMMENTS"] = "colorhub.edit_task_comments";
	UserPermission["ARCHIVE_TASK_COMMENTS"] = "colorhub.archive_task_comments";
	UserPermission["ADD_RISK_ASSESSMENT"] = "colorhub.add_risk_assessment";
	UserPermission["ADD_DOCUMENT"] = "colorhub.add_document";
	UserPermission["DELETE_DOCUMENT"] = "colorhub.delete_document";
	UserPermission["DOWNLOAD_DOCUMENT"] = "colorhub.download_document";
	UserPermission["EDIT_DOCUMENT"] = "colorhub.edit_document";
	UserPermission["FAX_DOCUMENT"] = "colorhub.fax_document";
	UserPermission["SHARE_DOCUMENT"] = "colorhub.share_document";
	UserPermission["VIEW_DOCUMENT"] = "colorhub.view_document";
	UserPermission["COLORHUB_ADMIN"] = "colorhub.admin_hub";
	UserPermission["VIEW_CONTRACT"] = "colorhub.view_contract";
	UserPermission["EDIT_CONTRACT"] = "colorhub.edit_contract";
	UserPermission["FULFILL_FULFILLMENT_ORDERS"] = "colorhub.fulfill_fulfillment_orders";
	UserPermission["VIEW_FULFILLMENT_ORDERS"] = "colorhub.view_fulfillment_orders";
	UserPermission["VIEW_INBOUND_DOCUMENT"] = "colorhub.view_inbound_document";
	UserPermission["EDIT_INBOUND_DOCUMENT"] = "colorhub.edit_inbound_document";
	UserPermission["VIEW_SMS_MESSAGES"] = "colorhub.view_sms_messages";
	UserPermission["SEND_SMS_MESSAGES"] = "colorhub.send_sms_messages";
	UserPermission["ACCESS_CANCER_COPILOT"] = "colorhub.access_cancer_copilot";
	UserPermission["ACCESS_METRIPORT"] = "colorhub.access_metriport";
	UserPermission["VIEW_ALL_HEALTHBOOK_CHATS"] = "colorhub.view_all_chats_healthbook";
	UserPermission["EDIT_PATIENT_EXPERIENCE_CONTENT"] = "colorhub.edit_patient_experience_content";
	return UserPermission;
}({});
var ScopedRoleType = /* @__PURE__ */ function(ScopedRoleType) {
	ScopedRoleType["COVID_OVERVIEW_DASHBOARD_VIEWER"] = "view_covid_overview_dashboard";
	ScopedRoleType["COVID_PATIENTS_DASHBOARD_VIEWER"] = "view_covid_patients_dashboard";
	ScopedRoleType["VACCINE_OVERVIEW_DASHBOARD_VIEWER"] = "view_vx_overview_dashboard";
	ScopedRoleType["VACCINE_PATIENTS_DASHBOARD_VIEWER"] = "view_vx_patients_dashboard";
	ScopedRoleType["VACCINE_AUDIT_DASHBOARD_VIEWER"] = "view_vx_audit_dashboard";
	ScopedRoleType["CONTACT_MANAGER"] = "contact_manager";
	ScopedRoleType["USER_MANAGER"] = "manage_users";
	ScopedRoleType["API_KEY_MANAGER"] = "api_key_manager";
	ScopedRoleType["LAB_OVERVIEW_DASHBOARD_VIEWER"] = "lab_overview_dashboard_viewer";
	ScopedRoleType["LAB_PHI_DASHBOARD_VIEWER"] = "lab_phi_dashboard_viewer";
	ScopedRoleType["ELIGIBILITY_DATA_MANAGER"] = "eligibility_data_manager";
	ScopedRoleType["RESEARCH_OVERVIEW_DASHBOARD_VIEWER"] = "research_overview_dashboard_viewer";
	ScopedRoleType["RESEARCH_CRO_OVERVIEW_DASHBOARD_VIEWER"] = "research_cro_overview_dashboard_viewer";
	ScopedRoleType["RESEARCH_PATIENTS_DASHBOARD_VIEWER"] = "research_patients_dashboard_viewer";
	ScopedRoleType["ESSENTIAL_CARE_OVERVIEW_DASHBOARD_VIEWER"] = "essential_care_overview_dashboard_viewer";
	ScopedRoleType["ESSENTIAL_CARE_PATIENTS_DASHBOARD_VIEWER"] = "essential_care_patients_dashboard_viewer";
	ScopedRoleType["CONSULTATIONS_OVERVIEW_DASHBOARD_VIEWER"] = "consultations_overview_dashboard_viewer";
	ScopedRoleType["CONSULTATIONS_PATIENTS_DASHBOARD_VIEWER"] = "consultations_patients_dashboard_viewer";
	ScopedRoleType["STI_OVERVIEW_DASHBOARD_VIEWER"] = "sti_overview_dashboard_viewer";
	ScopedRoleType["STI_PATIENTS_DASHBOARD_VIEWER"] = "sti_patients_dashboard_viewer";
	ScopedRoleType["ONSITE_BATCH_SHIPMENT_ADMIN"] = "onsite_batch_shipment_admin";
	ScopedRoleType["VACCINATION_PLATFORM_SITE_MANAGER"] = "vaccination_platform_site_manager";
	ScopedRoleType["VACCINATION_PLATFORM_SITE_STAFF"] = "vaccination_platform_site_staff";
	ScopedRoleType["VACCINATION_PLATFORM_SCRIBE"] = "vaccination_platform_scribe";
	ScopedRoleType["VACCINATION_PLATFORM_VACCINATING_CLINICIAN"] = "vaccination_platform_vaccinating_clinician";
	ScopedRoleType["VACCINATION_PLATFORM_SITE_SCHEDULER"] = "vaccination_platform_site_scheduler";
	ScopedRoleType["VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER"] = "vaccination_platform_site_inventory_manager";
	ScopedRoleType["VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER"] = "vaccination_platform_bulk_inventory_manager";
	ScopedRoleType["VACCINATION_PLATFORM_STAFF_MANAGER"] = "vaccination_platform_staff_manager";
	ScopedRoleType["VACCINATION_PLATFORM_SUPER_ADMIN"] = "vaccination_platform_super_admin";
	ScopedRoleType["PROMISE_OVERVIEW_DASHBOARD_VIEWER"] = "promise_overview_dashboard_viewer";
	ScopedRoleType["PROMISE_PARTICIPANTS_DASHBOARD_VIEWER"] = "promise_participants_dashboard_viewer";
	return ScopedRoleType;
}({});
var PermissionType = /* @__PURE__ */ function(PermissionType) {
	PermissionType["UNKNOWN"] = "UNKNOWN";
	PermissionType["ALLOWED"] = "ALLOWED";
	PermissionType["NOT_ALLOWED"] = "NOT_ALLOWED";
	return PermissionType;
}({});
//#endregion
//#region src/Auth/Context/apis.ts
var SESSIONS_ENDPOINT = "/api/v1/sessions";
var ACCOUNTS_ENDPOINT = "/api/v1/accounts";
function useAuthSession(queryOptions, baseUrl = "") {
	return useQuery({
		queryKey: [`${baseUrl}${SESSIONS_ENDPOINT}`],
		...queryOptions
	});
}
function useAuthenticatedUser(userIsAuthenticated, queryOptions, baseUrl = "") {
	return useQuery({
		queryKey: [`${baseUrl}${ACCOUNTS_ENDPOINT}`],
		...queryOptions,
		enabled: queryOptions.enabled || userIsAuthenticated
	});
}
//#endregion
//#region src/Auth/Context/constants.ts
var HTTP_UNATHENTICATED_STATUSES = new Set([401, 403]);
var AUTH_HEARTBEAT_INTERVAL_IN_MSEC = 6e4;
//#endregion
//#region src/Auth/Context/Segment/useIdentifySegmentUser.ts
function useIdentifySegmentUser() {
	const lastUserIdRef = useRef("");
	return { updateSegmentUser: useCallback((user, properties = {}) => {
		const patientId = user?.patient_id;
		if (patientId && patientId !== lastUserIdRef.current) {
			identifyAnalyticsUser({
				patientId,
				isColorEmployee: user.is_color_employee,
				properties
			});
			lastUserIdRef.current = patientId;
		}
	}, []) };
}
//#endregion
//#region src/Auth/Context/AuthContext.ts
/**
* When a user signs out, or the app wants to reset auth state, we should also
* clear out session storage to remove lingering data in the browser state.
*/
function clearSessionState() {
	clearBrowserStorage(BrowserStorageType.SESSION_STORAGE);
}
function useAuthState(props = {}) {
	const { baseUrl = "", sessionRefetchIntervalMs = AUTH_HEARTBEAT_INTERVAL_IN_MSEC, sessionRefetchIntervalInBackground = false, authUserStaleTimeMs = 0 } = props;
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const { updateSegmentUser } = useIdentifySegmentUser();
	const { updateLaunchDarkly } = useLaunchDarklyUpdater();
	const resetState = useCallback(() => {
		clearSessionState();
		setIsAuthenticated(false);
	}, []);
	const sessionResponse = useAuthSession({
		retry: false,
		refetchInterval: sessionRefetchIntervalMs,
		refetchIntervalInBackground: sessionRefetchIntervalInBackground,
		onSuccess: () => {
			setIsAuthenticated(true);
		},
		onError: (error) => {
			if (HTTP_UNATHENTICATED_STATUSES.has(error?.status)) setIsAuthenticated(false);
		},
		onSettled: async () => {
			await updateLaunchDarkly();
		}
	}, baseUrl);
	const { data: currentUser, refetch: fetchUser, isFetched: userIsFetched, remove: clearAuthenticatedUser } = useAuthenticatedUser(isAuthenticated, {
		refetchOnWindowFocus: isAuthenticated,
		staleTime: authUserStaleTimeMs,
		onSuccess: (user) => {
			updateSegmentUser(user);
			Sentry.setUser(user ? { id: user.id.toString() } : null);
		}
	}, baseUrl);
	const { isFetched: hasInitializedSession, refetch: updateSession, error: sessionError } = sessionResponse;
	const signIn = useCallback(async (email, password, nextUrl) => {
		try {
			const createSessionUrl = appendQueryParams(`${baseUrl}${SESSIONS_ENDPOINT}`, { next: nextUrl });
			const response = await ApiClient.post(createSessionUrl, { json: {
				email,
				password
			} });
			if (response?.is_mfa_required ?? false) {
				setIsAuthenticated(false);
				return {
					isSuccessful: false,
					mfaConfig: { factors: response?.factors ?? [] }
				};
			}
			if (response?.email_needs_confirmation ?? false) {
				setIsAuthenticated(false);
				return {
					isSuccessful: false,
					emailNeedsConfirmation: true
				};
			}
			trackAnalyticsEvent("Sign In - Succeeded", { is_mfa: "no" });
			setIsAuthenticated(true);
			await updateLaunchDarkly();
			return { isSuccessful: true };
		} catch (error) {
			trackAnalyticsEvent("Sign In - Failed", {
				error: error instanceof Error ? error.message : error?.toString?.() ?? "",
				status: error instanceof ApiError ? error.status : ""
			});
			setIsAuthenticated(false);
			throw error;
		}
	}, [updateLaunchDarkly, baseUrl]);
	const signOut = useCallback(async () => {
		try {
			await ApiClient.delete(`${baseUrl}${SESSIONS_ENDPOINT}`);
		} catch (error) {
			if (error.status !== 401) throw error;
		}
		clearAuthenticatedUser();
		clearSessionState();
		resetAnalyticsUser();
		Sentry.setUser(null);
		setIsAuthenticated(false);
		try {
			await updateSession();
		} catch {}
	}, [
		clearAuthenticatedUser,
		updateSession,
		baseUrl
	]);
	/**
	* The session response could fail for a number of reasons, including intermittent network connections.
	* The error is considered as unauthenticated if the API status code is one of `HTTP_UNATHENTICATED_STATUSES`
	*/
	const sessionErrorIsUnauthenticatedStatus = hasInitializedSession && sessionError != null && HTTP_UNATHENTICATED_STATUSES.has(sessionError.status);
	const resetStateAndUpdateSession = useCallback(async () => {
		resetState();
		const updateSessionResponse = await updateSession();
		let attemptCount = 1;
		let hasSucceeded = updateSessionResponse.isSuccess;
		if (!hasSucceeded) {
			const retriedUpdateSessionResponse = await updateSession();
			attemptCount += 1;
			hasSucceeded = retriedUpdateSessionResponse.isSuccess;
		}
		trackAnalyticsEvent("Session Refresh", {
			status: hasSucceeded ? "succeeded" : "failed",
			attempt_count: attemptCount
		});
	}, [resetState, updateSession]);
	return {
		currentUser,
		hasInitializedUser: userIsFetched || sessionErrorIsUnauthenticatedStatus,
		hasInitializedSession,
		isAuthenticated,
		resetStateAndUpdateSession,
		signIn,
		signOut,
		fetchUser
	};
}
var dest = constate(useAuthState);
var AuthProvider = dest[0];
var useAuthContext = dest[1];
//#endregion
export { ALL_USER_ROLES as a, GENETICS_USER_ROLES as c, UserPermission as d, UserRole as f, SESSIONS_ENDPOINT as i, PermissionType as l, useAuthContext as n, CollectionSiteRole as o, ACCOUNTS_ENDPOINT as r, CollectionSiteRoleCategory as s, AuthProvider as t, ScopedRoleType as u };

//# sourceMappingURL=AuthContext-BU6h5D0G.js.map